import React, { useState } from 'react';
import { Card, Table, Tag, Button, Space, Typography, Alert, Spin, Tooltip, Modal, App, Row, Col, Statistic, Divider, Tabs } from 'antd';
import {
  DollarOutlined,
  UserOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
  CalculatorOutlined,
  LockOutlined,
  HistoryOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useGetAllOpenCashRegistersQuery, useAddCashMovementMutation, useCloseCashRegisterMutation } from '../../services/cashRegisterApi';
import { useCurrency } from '../../hooks/useCurrency';
import { useAppSelector } from '../../hooks/redux';
import { useResponsive } from '../../hooks/useResponsive';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { CashRegisterDto } from '../../types/cashRegister';
import CashMovementsList from './CashMovementsList';
import { getOperatingTime } from './CashRegisterCard';
import './cashRegister.css';
import AddCashMovementModal from './AddCashMovementModal';
import CloseCashRegisterModal from './CloseCashRegisterModal';
import CashCountModal from './CashCountModal';
import CashRegisterHistoryTab from './CashRegisterHistoryTab';

const { Text } = Typography;

const AdminAllCashRegistersView: React.FC = () => {
  const { message } = App.useApp();
  const { data: openRegisters, isLoading, error, refetch } = useGetAllOpenCashRegistersQuery();
  const { currencySymbol } = useCurrency();
  const { currentRestaurant } = useAppSelector((state) => state.restaurant);
  const { isMobile } = useResponsive();
  const { formatInTimeZone } = useRestaurantTimeZone();
  const [addCashMovement, { isLoading: isAddingMovement }] = useAddCashMovementMutation();
  const [closeCashRegister, { isLoading: isClosing }] = useCloseCashRegisterMutation();

  const [selectedRegister, setSelectedRegister] = useState<CashRegisterDto | null>(null);
  const [isMovementsModalVisible, setIsMovementsModalVisible] = useState(false);
  const [isAddIncomeModalVisible, setIsAddIncomeModalVisible] = useState(false);
  const [isAddExpenseModalVisible, setIsAddExpenseModalVisible] = useState(false);
  const [isCashCountModalVisible, setIsCashCountModalVisible] = useState(false);
  const [isCloseModalVisible, setIsCloseModalVisible] = useState(false);
  const [movementType, setMovementType] = useState<'income' | 'expense' | null>(null);
  const [activeTab, setActiveTab] = useState<string>('open');

  const handleViewMovements = (register: CashRegisterDto) => {
    setSelectedRegister(register);
    setIsMovementsModalVisible(true);
  };

  const handleAddIncome = (register: CashRegisterDto) => {
    setSelectedRegister(register);
    setMovementType('income');
    setIsAddIncomeModalVisible(true);
  };

  const handleAddExpense = (register: CashRegisterDto) => {
    setSelectedRegister(register);
    setMovementType('expense');
    setIsAddExpenseModalVisible(true);
  };

  const handleSubmitMovement = async (values: { amount: number; reason: string; reference?: string; type: string }) => {
    if (!selectedRegister?.id) {
      message.error('No se ha seleccionado una caja registradora');
      return;
    }
    try {
      await addCashMovement({
        registerId: selectedRegister.id,
        data: {
          cashRegisterId: selectedRegister.id,
          type: values.type,
          amount: values.amount,
          reason: values.reason,
          reference: values.reference,
        }
      }).unwrap();
      message.success(
        movementType === 'income'
          ? 'Ingreso registrado exitosamente'
          : 'Retiro registrado exitosamente'
      );
      setIsAddIncomeModalVisible(false);
      setIsAddExpenseModalVisible(false);
      setMovementType(null);
      refetch();
    } catch (err: any) {
      message.error(err?.data?.message || 'Error al registrar el movimiento');
    }
  };

  const handleCashCount = (register: CashRegisterDto) => {
    setSelectedRegister(register);
    setIsCashCountModalVisible(true);
  };

  const handleConfirmCashCount = async () => {
    setIsCashCountModalVisible(false);
    message.success('Arqueo registrado correctamente');
  };

  const handleCloseCashRegister = (register: CashRegisterDto) => {
    setSelectedRegister(register);
    setIsCloseModalVisible(true);
  };

  const confirmCloseCashRegister = async (finalAmount: number, notes?: string) => {
    if (!selectedRegister) return;
    try {
      await closeCashRegister({
        registerId: selectedRegister.id,
        data: {
          finalAmount,
          notes,
        }
      }).unwrap();
      message.success('Caja cerrada exitosamente');
      setIsCloseModalVisible(false);
      setSelectedRegister(null);
      refetch();
    } catch (error: any) {
      const errorMessage = error?.data?.message
        || error?.message
        || 'Error al cerrar la caja registradora';
      message.error(errorMessage, 5);
    }
  };

  // Mobile-optimized cash register cards
  const renderCashRegisterCards = () => (
    <Row gutter={[16, 16]}>
      {openRegisters?.map((register: CashRegisterDto) => {
        const hours = dayjs().diff(dayjs(register.openTime), 'hour');
        return (
          <Col xs={24} sm={12} lg={8} key={register.id}>
            <Card
              size="small"
              title={
                <Space>
                  <UserOutlined />
                  <Text strong>{register.userName || 'Usuario'}</Text>
                </Space>
              }
              extra={
                <Tag color={hours > 12 ? 'orange' : 'blue'}>
                  {getOperatingTime(register.openTime)}
                </Tag>
              }
            >
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div>
                  <Text type="secondary">Hora de Apertura:</Text>
                  <br />
                  <Space>
                    <ClockCircleOutlined />
                    <Text>{formatInTimeZone(register.openTime, 'DD/MM/YYYY HH:mm')}</Text>
                  </Space>
                </div>

                <Divider style={{ margin: '8px 0' }} />

                <Row gutter={8}>
                  <Col span={12}>
                    <Statistic
                      title="Monto Inicial"
                      value={register.initialAmount}
                      precision={2}
                      prefix={currencySymbol}
                      valueStyle={{ fontSize: '14px' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Ventas"
                      value={register.summary?.totalAllSales || 0}
                      precision={2}
                      prefix={currencySymbol}
                      valueStyle={{ fontSize: '14px', color: 'var(--color-success)' }}
                    />
                  </Col>
                </Row>

                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">Efectivo Esperado:</Text>
                  <br />
                  <Text strong style={{ color: 'var(--color-success)', fontSize: '16px' }}>
                    {currencySymbol}{(register.expectedAmount || 0).toFixed(2)}
                  </Text>
                </div>

                <Divider style={{ margin: '8px 0' }} />

                <Space wrap size="small" style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Tooltip title="Ver Movimientos">
                    <Button
                      type="text"
                      icon={<EyeOutlined />}
                      onClick={() => handleViewMovements(register)}
                      size="small"
                    >
                      Movimientos
                    </Button>
                  </Tooltip>
                  <Tooltip title="Agregar Ingreso">
                    <Button
                      type="text"
                      icon={<PlusCircleOutlined />}
                      style={{ color: 'var(--color-success)' }}
                      onClick={() => handleAddIncome(register)}
                      size="small"
                    >
                      Ingreso
                    </Button>
                  </Tooltip>
                  <Tooltip title="Agregar Gasto">
                    <Button
                      type="text"
                      icon={<MinusCircleOutlined />}
                      danger
                      onClick={() => handleAddExpense(register)}
                      size="small"
                    >
                      Gasto
                    </Button>
                  </Tooltip>
                  <Tooltip title="Contar Efectivo">
                    <Button
                      type="text"
                      icon={<CalculatorOutlined />}
                      onClick={() => handleCashCount(register)}
                      size="small"
                    >
                      Arqueo
                    </Button>
                  </Tooltip>
                  <Tooltip title="Cerrar Caja">
                    <Button
                      type="text"
                      icon={<LockOutlined />}
                      danger
                      onClick={() => handleCloseCashRegister(register)}
                      size="small"
                    >
                      Cerrar
                    </Button>
                  </Tooltip>
                </Space>
              </Space>
            </Card>
          </Col>
        );
      })}
    </Row>
  );

  const columns = [
    {
      title: 'Usuario',
      dataIndex: 'userName',
      key: 'userName',
      render: (userName: string) => (
        <Space>
          <UserOutlined />
          <Text strong>{userName || 'Usuario'}</Text>
        </Space>
      ),
    },
    {
      title: 'Hora de Apertura',
      dataIndex: 'openTime',
      key: 'openTime',
      render: (openTime: string) => (
        <Space>
          <ClockCircleOutlined />
          <Text>{formatInTimeZone(openTime, 'DD/MM/YYYY HH:mm')}</Text>
        </Space>
      ),
    },
    {
      title: 'Monto Inicial',
      dataIndex: 'initialAmount',
      key: 'initialAmount',
      align: 'right' as const,
      render: (amount: number) => (
        <Text strong>{currencySymbol}{amount.toFixed(2)}</Text>
      ),
    },
    {
      title: 'Ventas Totales',
      dataIndex: ['summary', 'totalAllSales'],
      key: 'totalAllSales',
      align: 'right' as const,
      render: (amount: number = 0) => (
        <Tag color="green">{currencySymbol}{amount.toFixed(2)}</Tag>
      ),
    },
    {
      title: 'Efectivo Esperado',
      dataIndex: 'expectedAmount',
      key: 'expectedAmount',
      align: 'right' as const,
      render: (amount: number = 0) => (
        <Text strong style={{ color: 'var(--color-success)' }}>{currencySymbol}{amount.toFixed(2)}</Text>
      ),
    },
    {
      title: 'Duración',
      dataIndex: 'openTime',
      key: 'duration',
      render: (openTime: string) => {
        const hours = dayjs().diff(dayjs(openTime), 'hour');
        return (
          <Tag color={hours > 12 ? 'orange' : 'blue'}>
            {getOperatingTime(openTime)}
          </Tag>
        );
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center' as const,
      render: (_: any, record: CashRegisterDto) => (
        <Space size="small">
          <Tooltip title="Ver Movimientos">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewMovements(record)}
            />
          </Tooltip>
          <Tooltip title="Agregar Ingreso">
            <Button
              type="text"
              icon={<PlusCircleOutlined />}
              style={{ color: 'var(--color-success)' }}
              onClick={() => handleAddIncome(record)}
            />
          </Tooltip>
          <Tooltip title="Agregar Gasto">
            <Button
              type="text"
              icon={<MinusCircleOutlined />}
              danger
              onClick={() => handleAddExpense(record)}
            />
          </Tooltip>
          <Tooltip title="Contar Efectivo">
            <Button
              type="text"
              icon={<CalculatorOutlined />}
              onClick={() => handleCashCount(record)}
            />
          </Tooltip>
          <Tooltip title="Cerrar Caja">
            <Button
              type="text"
              icon={<LockOutlined />}
              danger
              onClick={() => handleCloseCashRegister(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (isLoading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Cargando cajas abiertas...</Text>
          </div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Alert
          message="Error al cargar las cajas"
          description="No se pudieron cargar las cajas abiertas. Por favor, intenta de nuevo."
          type="error"
          showIcon
          action={
            <Button size="small" onClick={() => refetch()}>
              Reintentar
            </Button>
          }
        />
      </Card>
    );
  }

  // Floor totals for the pulse chips
  const totalExpected = (openRegisters ?? []).reduce((acc, r) => acc + (r.expectedAmount ?? 0), 0);
  const totalSales = (openRegisters ?? []).reduce((acc, r) => acc + (r.summary?.totalAllSales ?? 0), 0);

  // Content for the "Cajas Abiertas" tab
  const renderOpenRegistersContent = () => (
    <>
      {!openRegisters || openRegisters.length === 0 ? (
        <Alert
          message="No hay cajas abiertas"
          description="Actualmente no hay ninguna caja abierta en el restaurante."
          type="info"
          showIcon
        />
      ) : (
        <>
          {/* Floor pulse: what the admin came to see, before the row-by-row detail */}
          <div className="cash-bar" style={{ marginBottom: 12 }}>
            <span className="cash-bar__chip">
              <span className="cash-dot" />
              <b>{openRegisters.length}</b>&nbsp;{openRegisters.length === 1 ? 'caja abierta' : 'cajas abiertas'}
            </span>
            <span className="cash-bar__chip">
              efectivo esperado&nbsp;<b>{currencySymbol}{totalExpected.toFixed(2)}</b>
            </span>
            <span className="cash-bar__chip">
              ventas&nbsp;<b>{currencySymbol}{totalSales.toFixed(2)}</b>
            </span>
          </div>
          {isMobile ? (
            renderCashRegisterCards()
          ) : (
            <Table
              columns={columns}
              dataSource={openRegisters}
              rowKey="id"
              pagination={false}
              scroll={{ x: 'max-content' }}
            />
          )}
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 10 }}>
            Las acciones que realices sobre cajas de otros usuarios quedan registradas en el historial de auditoría.
          </Text>
        </>
      )}
    </>
  );

  const tabItems = [
    {
      key: 'open',
      label: (
        <span>
          <DollarOutlined />
          {isMobile ? ' Abiertas' : ' Cajas Abiertas'}
          {openRegisters && openRegisters.length > 0 && (
            <Tag color="blue" style={{ marginLeft: 8 }}>{openRegisters.length}</Tag>
          )}
        </span>
      ),
      children: renderOpenRegistersContent(),
    },
    {
      key: 'history',
      label: (
        <span>
          <HistoryOutlined />
          {isMobile ? ' Historial' : ' Historial de Cajas'}
        </span>
      ),
      children: <CashRegisterHistoryTab restaurantId={currentRestaurant?.id || ''} />,
    },
  ];

  return (
    <>
      {/* No wrapping Card/title: the page already sits under the "Todas las Cajas"
          tab — a third level of chrome said "Gestión de Cajas" for the second time. */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size={isMobile ? 'small' : 'middle'}
        tabBarExtraContent={
          activeTab === 'open' && (
            <Tooltip title="Actualizar">
              <Button
                icon={<ReloadOutlined />}
                onClick={() => refetch()}
                size={isMobile ? 'small' : 'middle'}
                type="text"
              />
            </Tooltip>
          )
        }
      />

      {/* Movements Modal */}
      <Modal
        title={`Movimientos de ${selectedRegister?.userName || 'Caja'}`}
        open={isMovementsModalVisible}
        onCancel={() => setIsMovementsModalVisible(false)}
        footer={null}
        width={isMobile ? '95%' : 800}
        style={{ top: isMobile ? 20 : undefined }}
      >
        {selectedRegister && (
          <CashMovementsList cashRegisterId={selectedRegister.id} />
        )}
      </Modal>

      {/* Add Income Modal */}
      <AddCashMovementModal
        visible={isAddIncomeModalVisible}
        movementType={movementType}
        onCancel={() => {
          setIsAddIncomeModalVisible(false);
          setMovementType(null);
        }}
        onSubmit={handleSubmitMovement}
        loading={isAddingMovement}
        cashRegister={selectedRegister}
      />

      {/* Add Expense Modal */}
      <AddCashMovementModal
        visible={isAddExpenseModalVisible}
        movementType={movementType}
        onCancel={() => {
          setIsAddExpenseModalVisible(false);
          setMovementType(null);
        }}
        onSubmit={handleSubmitMovement}
        loading={isAddingMovement}
        cashRegister={selectedRegister}
      />

      {/* Cash Count Modal */}
      <CashCountModal
        visible={isCashCountModalVisible}
        expectedAmount={selectedRegister?.expectedAmount ?? 0}
        cashRegister={selectedRegister}
        onCancel={() => setIsCashCountModalVisible(false)}
        onConfirm={handleConfirmCashCount}
      />

      {/* Close Cash Register Modal */}
      <CloseCashRegisterModal
        visible={isCloseModalVisible}
        cashRegister={selectedRegister}
        onCancel={() => {
          setIsCloseModalVisible(false);
          setSelectedRegister(null);
        }}
        onConfirm={confirmCloseCashRegister}
        loading={isClosing}
      />
    </>
  );
};

export default AdminAllCashRegistersView;
